"use client";

import { motion } from "framer-motion";
import {
  Sparkles,
} from "lucide-react";
import type { Gift } from "@/types";
import {
  getExperienceData,
  type CuriositiesExperienceData,
} from "@/data/experienceDataSchema";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CoupleWrappedSection as CoupleWrappedSectionImpl } from "@/components/published-experiences/sections/CoupleWrappedSection";
import { AchievementsSection as AchievementsSectionImpl } from "@/components/published-experiences/sections/AchievementsSection";
import { CuriositiesQuiz } from "@/components/published-experiences/sections/CuriositiesQuiz";
import { GallerySection as GallerySectionImpl } from "@/components/published-experiences/sections/GallerySection";
import { GiftTreeSection as GiftTreeSectionImpl } from "@/components/published-experiences/sections/GiftTreeSection";
import { MapJourneySection as MapJourneySectionImpl } from "@/components/published-experiences/sections/MapJourneySection";
import { StarsMapSection as StarsMapSectionImpl } from "@/components/published-experiences/sections/StarsMapSection";
import { SurpriseWheelSection as SurpriseWheelSectionImpl } from "@/components/published-experiences/sections/SurpriseWheelSection";
import { TimelineSection as TimelineSectionImpl } from "@/components/published-experiences/sections/TimelineSection";
import { WordGameSection as WordGameSectionImpl } from "@/components/published-experiences/sections/WordGameSection";

interface Palette {
  accent: string;
  accentRgb: string;
}

interface ShellProps {
  icon: React.ReactNode;
  label: string;
  palette: Palette;
  delay?: number;
  children: React.ReactNode;
}

function SectionShell({ icon, label, palette, delay = 0, children }: ShellProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="rounded-2xl overflow-hidden"
      style={{
        background: `linear-gradient(135deg, rgba(${palette.accentRgb},0.10) 0%, rgba(${palette.accentRgb},0.04) 100%)`,
        border: `1px solid rgba(${palette.accentRgb},0.2)`,
      }}
    >
      <div className="px-3 pt-3 pb-2 flex items-center gap-2">
        <span style={{ color: palette.accent }}>{icon}</span>
        <span
          className="text-[10px] font-bold uppercase tracking-wider"
          style={{ color: palette.accent }}
        >
          {label}
        </span>
      </div>
      <div className="px-3 pb-3 text-white/85 text-[12px] leading-relaxed">
        {children}
      </div>
    </motion.div>
  );
}

interface SectionProps {
  palette: Palette;
  gift: Partial<Gift>;
}

function TimelineSection({ palette, gift }: SectionProps) {
  return (
    <TimelineSectionImpl gift={gift} variant="spotify" palette={palette} />
  );
}

function StarsMapSection({ palette, gift }: SectionProps) {
  return <StarsMapSectionImpl gift={gift} variant="spotify" palette={palette} />;
}

function GallerySection({ palette, gift }: SectionProps) {
  return <GallerySectionImpl gift={gift} variant="spotify" palette={palette} />;
}

function WordGameSection({ palette, gift }: SectionProps) {
  return <WordGameSectionImpl gift={gift} variant="spotify" palette={palette} />;
}

function MapJourneySection({ palette, gift }: SectionProps) {
  return (
    <MapJourneySectionImpl gift={gift} variant="spotify" palette={palette} />
  );
}

function SurpriseWheelSection({ palette, gift }: SectionProps) {
  return (
    <SurpriseWheelSectionImpl gift={gift} variant="spotify" palette={palette} />
  );
}

function GiftTreeSection({ palette, gift }: SectionProps) {
  return (
    <GiftTreeSectionImpl gift={gift} variant="spotify" palette={palette} />
  );
}

function CoupleWrappedSection({ palette, gift }: SectionProps) {
  return (
    <CoupleWrappedSectionImpl gift={gift} variant="spotify" palette={palette} />
  );
}

function CuriositiesSection({ palette, gift }: SectionProps) {
  const customData = getExperienceData<CuriositiesExperienceData>(
    gift,
    "curiosities"
  );
  const label = customData?.title?.trim() || "Curiosidades";
  return (
    <SectionShell
      icon={<Sparkles className="h-3 w-3" />}
      label={label}
      palette={palette}
    >
      <CuriositiesQuiz gift={gift} variant="spotify" palette={palette} />
    </SectionShell>
  );
}

function AchievementsSection({ palette, gift }: SectionProps) {
  return (
    <AchievementsSectionImpl gift={gift} variant="spotify" palette={palette} />
  );
}

interface Props {
  selectedExperiences: string[] | undefined;
  palette: Palette;
  gift: Partial<Gift>;
}

// Wrapper que isola cada seção em seu próprio ErrorBoundary — se uma
// experiência crashar (ex: dados inválidos do experienceData salvo
// antes de uma mudança de schema), só ela some, as outras renderizam.
function SafeSection({
  experienceId,
  children,
}: {
  experienceId: string;
  children: React.ReactNode;
}) {
  return (
    <ErrorBoundary
      scope={`experience-${experienceId}`}
      fallback={
        <div
          className="rounded-2xl p-4 text-center text-[11px] text-white/50"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          Não conseguimos carregar esta experiência ({experienceId}).
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
}

export function SpotifyExperienceSections({
  selectedExperiences,
  palette,
  gift,
}: Props) {
  if (!selectedExperiences || selectedExperiences.length === 0) return null;
  return (
    <>
      {selectedExperiences.includes("timeline") && (
        <SafeSection experienceId="timeline">
          <TimelineSection palette={palette} gift={gift} />
        </SafeSection>
      )}
      {selectedExperiences.includes("gallery") && (
        <SafeSection experienceId="gallery">
          <GallerySection palette={palette} gift={gift} />
        </SafeSection>
      )}
      {selectedExperiences.includes("stars-map") && (
        <SafeSection experienceId="stars-map">
          <StarsMapSection palette={palette} gift={gift} />
        </SafeSection>
      )}
      {selectedExperiences.includes("word-game") && (
        <SafeSection experienceId="word-game">
          <WordGameSection palette={palette} gift={gift} />
        </SafeSection>
      )}
      {selectedExperiences.includes("map-journey") && (
        <SafeSection experienceId="map-journey">
          <MapJourneySection palette={palette} gift={gift} />
        </SafeSection>
      )}
      {selectedExperiences.includes("surprise-wheel") && (
        <SafeSection experienceId="surprise-wheel">
          <SurpriseWheelSection palette={palette} gift={gift} />
        </SafeSection>
      )}
      {selectedExperiences.includes("gift-tree") && (
        <SafeSection experienceId="gift-tree">
          <GiftTreeSection palette={palette} gift={gift} />
        </SafeSection>
      )}
      {selectedExperiences.includes("couple-wrapped") && (
        <SafeSection experienceId="couple-wrapped">
          <CoupleWrappedSection palette={palette} gift={gift} />
        </SafeSection>
      )}
      {selectedExperiences.includes("curiosities") && (
        <SafeSection experienceId="curiosities">
          <CuriositiesSection palette={palette} gift={gift} />
        </SafeSection>
      )}
      {selectedExperiences.includes("achievements") && (
        <SafeSection experienceId="achievements">
          <AchievementsSection palette={palette} gift={gift} />
        </SafeSection>
      )}
    </>
  );
}
